<template>
  <ActionButton
    v-for="btn in visibleButtons"
    :key="btn.label"
    modification="editor"
    :disabled="btn.disabled"
    :title="btn.label"
    @click="btn.emit"
  >
    <template #icon>
      <Icon
        :icon="btn.icon"
        size="small"
      />
    </template>
  </ActionButton>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import ActionButton from '@/components/basic/ActionButton/ActionButton.vue'
import Icon from '@/components/basic/Icon/Icon.vue'
import { ENewsStatusReverse } from '@/lib/references.ts'
import { ACCESS } from '@/lib/accessCodes.ts'
import { useAccess } from '@/ContextTab/stores/auth/useAccess.ts'
import type { NewsVersionStatus } from '@/api/generated/v2'


interface IActionButton {
  label: string
  icon: string
  disabled: boolean
  visible: boolean
  emit: typeof emit
}

const { statusId, isRevoked, versionUserId } = defineProps<{
  statusId: NewsVersionStatus
  isRevoked: boolean
  versionUserId: number | undefined
}>()

const emit = defineEmits<{
  (e: 'publish'): void
  (e: 'send'): void
  (e: 'sendToAsoi'): void
  (e: 'sendCopy'): void
  (e: 'delete'): void
  (e: 'dispose'): void
  (e: 'extend'): void
  (e: 'pick'): void
  (e: 'republish'): void
  (e: 'addCodes'): void
  (e: 'embargo'): void
  (e: 'embargo'): void
  (e: 'flash'): void
  (e: 'revoke'): void
}>()

const { userHasAllPermissions, userId } = useAccess()

/* Правила видимости кнопок описаны здесь - https://confluence.interfax.ru/spaces/PUB/pages/279355114 */
const visibleButtons = computed<IActionButton[]>(() => ([
  {
    label: 'Опубликовать',
    icon: 'publish',
    disabled: true,
    visible: userHasAllPermissions([ACCESS.NEWS.PUBLISHING])
      && (statusId === ENewsStatusReverse.DRAFT || statusId === ENewsStatusReverse.ON_EDIT),
    emit: () => emit('publish'),
  },
  {
    label: 'Взять в работу',
    icon: 'pencil',
    disabled: false,
    visible: statusId === ENewsStatusReverse.WAITS_FOR_EDIT || statusId === ENewsStatusReverse.WAITS_FOR_PUBLICATION,
    emit: () => emit('pick'),
  },
  {
    label: 'Вернуть в работу',
    icon: 'arrow-up',
    disabled: false,
    visible: (statusId === ENewsStatusReverse.DISPOSED) && userHasAllPermissions([ACCESS.NEWS.DUMP_EXTRACT]),
    emit: () => emit('pick'),
  },
  {
    label: 'Повтор',
    icon: 'republish',
    disabled: false,
    visible: (statusId === ENewsStatusReverse.WAITS_FOR_PUBLICATION || statusId === ENewsStatusReverse.PUBLISHED)
      && ((!isRevoked && userHasAllPermissions([ACCESS.NEWS.REPUBLISH]))),
    emit: () => emit('republish'),
  },
  {
    label: 'Расширить',
    icon: 'extension',
    disabled: false,
    visible: userHasAllPermissions([ACCESS.NEWS.EXTENSION]) && !isRevoked,
    emit: () => emit('extend'),
  },
  {
    label: 'Добавить коды',
    icon: 'plus',
    disabled: true,
    visible: statusId === ENewsStatusReverse.PUBLISHED
      && userHasAllPermissions([ACCESS.NEWS.REPUBLISH]) && !isRevoked,
    emit: () => emit('addCodes'),
  },
  {
    label: 'Запланировать публикацию',
    icon: 'time',
    disabled: true,
    visible: false,
    emit: () => emit('embargo'),
  },
  {
    label: 'Отправить',
    icon: 'send',
    disabled: false,
    visible: (versionUserId === userId.value
      && (statusId === ENewsStatusReverse.DRAFT || statusId === ENewsStatusReverse.ON_EDIT))
    || statusId === ENewsStatusReverse.WAITS_FOR_PUBLICATION
    || statusId === ENewsStatusReverse.PUBLISHED,
    emit: (statusId === ENewsStatusReverse.WAITS_FOR_PUBLICATION
      || statusId === ENewsStatusReverse.PUBLISHED)
      ? () => emit('sendCopy')
      : () => emit('send'),
  },
  {
    label: 'Отправить в АСОИ',
    icon: 'send',
    disabled: false,
    visible: true,
    emit: () => emit('sendToAsoi'),
  },
  {
    label: 'Аннулировать',
    icon: 'ban',
    disabled: false,
    visible: statusId === ENewsStatusReverse.PUBLISHED && !isRevoked,
    emit: () => emit('revoke'),
  },
  {
    label: 'Удалить',
    icon: 'trash',
    disabled: false,
    visible: statusId === ENewsStatusReverse.DRAFT && versionUserId === userId.value,
    emit: () => emit('delete'),
  },
  {
    label: 'Отклонить',
    icon: 'trash',
    disabled: false,
    visible: userHasAllPermissions([ACCESS.NEWS.DUMP_SEND])
      && (statusId === ENewsStatusReverse.ON_EDIT || statusId === ENewsStatusReverse.WAITS_FOR_PUBLICATION),
    emit: () => emit('dispose'),
  },
  {
    label: 'Молния на выпуск',
    icon: 'exclamation-mark',
    disabled: true,
    visible: userHasAllPermissions([ACCESS.NEWS.FLASH_SEND]),
    emit: () => emit('flash'),
  },
  {
    label: 'Восстановить',
    icon: 'restore',
    disabled: false,
    visible: userHasAllPermissions([ACCESS.NEWS.RESTORE_REVOKED]) && isRevoked,
    emit: () => emit('republish'),
  },
] satisfies IActionButton[]).filter((x) => x.visible))
</script>
